/* Shared display formatters for the screens. Lives apart from `index.tsx` so a
   screen can import it without importing the screen registry that imports it. */

/**
 * The timezone the day boundary is drawn in — a mirror of the server's
 * `MILO_TIMEZONE`, set in `web/.env` as `VITE_MILO_TIMEZONE`.
 *
 * Every date in this app belongs to the server: a write with no `date` lands on
 * `config.today()` in `MILO_TIMEZONE`, and "TODAY SO FAR" is read with `days=0`,
 * meaning "since the server's today". The heading is the only date the browser
 * used to decide for itself, so after local midnight — or for a user east of the
 * server, or travelling — the heading claimed one day while the panel below it,
 * and the save button beside it, meant another.
 *
 * A build-time mirror of a server setting is not free: it is a second copy of
 * one fact, and a stale copy shows the wrong day. It is acceptable *here* and
 * nowhere else in this screen, because it decides only what a label says. It
 * must never decide what gets fetched or what gets written — those stay with the
 * server, which is why `loadFood` still asks for `days=0` rather than computing
 * a date from this. Unset, the heading falls back to the browser's clock, which
 * is exactly the previous behaviour.
 */
const DISPLAY_TZ = readDisplayTz()

function readDisplayTz(): string | undefined {
  const tz = (import.meta.env.VITE_MILO_TIMEZONE as string | undefined)?.trim()
  if (!tz) return undefined
  try {
    // A bad IANA name throws RangeError on every later format call, which would
    // take the whole screen down over a typo in an env file. Prove it once.
    new Date().toLocaleDateString('en-US', { timeZone: tz })
    return tz
  } catch {
    console.warn(`VITE_MILO_TIMEZONE is not a valid timezone: ${tz}. Using the browser's.`)
    return undefined
  }
}

/** "TUE AUG 04" — the one mono date format, from a Date and an optional zone. */
function mono(d: Date, timeZone?: string): string {
  return d
    .toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    })
    .replace(',', '')
    .toUpperCase()
}

/** Today, in the server's timezone when it is configured. Every screen heading. */
export function headingDate(): string {
  return mono(new Date(), DISPLAY_TZ)
}

/**
 * The same mono format, from an API date string.
 *
 * Built from the parts rather than `new Date(iso)`, which reads a bare
 * `YYYY-MM-DD` as UTC midnight and so renders the previous day everywhere west
 * of Greenwich — which is everywhere Milo runs.
 *
 * Deliberately does not pass `DISPLAY_TZ`: this Date is already local midnight
 * on the calendar day the server named, so re-projecting it into another zone
 * would shift the very day it was built to preserve.
 */
export function monoDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso.toUpperCase()
  return mono(new Date(year, month - 1, day))
}
